import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import cx from 'clsx';
import { useForm } from 'react-hook-form';
import { useSegment } from '@repo/poly-hooks/segment';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { useToast } from '@repo/design-system-kit';
import { Error } from 'receivable-invoices-js/react/components/mandates';
import { Loading } from 'receivable-invoices-js/react/components/shared/loading';
import { useLocaleManager } from 'receivable-invoices-js/react/hooks/use-locale-manager';
import { useDeviceManager } from 'receivable-invoices-js/react/hooks/use-device-manager';
import {
  Details,
  Footer,
  Header,
  TermsAndConditionsCheckbox,
  ToggleSection,
} from 'receivable-invoices-js/react/components/sdd-payment-link';
import { createTrackingProperties } from 'receivable-invoices-js/react/utils/mandates';
import { useRouteQuery } from './use-route-query';
import styles from './layout.module.css';

type MandatesRouteProps = Record<string, never>;

export interface SignMandateFormData {
  clientAddress: string;
  clientZipCode: string;
  clientCity: string;
  clientCountryCode: string;
  clientIban: string;
  clientProvinceCode: string;
  termsAndConditions: boolean;
}

export function MandatesRoute(_props: MandatesRouteProps): ReactNode {
  const { showToast } = useToast();
  const segment = useSegment();
  const localeManager = useLocaleManager();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const deviceManager = useDeviceManager();

  const [readonlyClientAddress, setReadonlyClientAddress] = useState<string | undefined>();
  const [readonlyClientZipCode, setReadonlyClientZipCode] = useState<string | undefined>();
  const [readonlyClientCity, setReadonlyClientCity] = useState<string | undefined>();
  const [readonlyClientCountryCode, setReadonlyClientCountryCode] = useState<string | undefined>();
  const [termsAndConditionsError, setTermsAndConditionsError] = useState<string | undefined>();
  const signatureDate = dayjs().toISOString();

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<SignMandateFormData>({
    defaultValues: {
      clientAddress: '',
      clientZipCode: '',
      clientCity: '',
      clientCountryCode: '',
      clientIban: '',
      clientProvinceCode: '',
      termsAndConditions: false,
    },
  });
  const formValues = watch();

  const {
    errorType,
    isError,
    isLoading,
    mandate,
    refetch,
    signMandate,
    isSigningMandate,
    fieldErrors,
    clearFieldErrors,
    clearFieldError,
    setFieldError,
    addClientSideErrors,
  } = useRouteQuery({
    onSignSuccess: () => {
      if (mandate) {
        void navigate(`/mandates/${mandate.mandateId}/success`);
      }
    },
    onSignError: () => {
      showToast({
        type: 'error',
        text: formatMessage({ id: 'sdd-collections.toasts.error.sdd-setup-failed' }),
      });
    },
  });

  useEffect(() => {
    if (mandate?.clientLocale) {
      localeManager.setLocale(mandate.clientLocale);
    }

    if (mandate) {
      const getTrackingProperties = createTrackingProperties({
        isSddRecurring: mandate.isSddRecurring,
        addressWasPrefilled: mandate.addressWasPrefilled,
        scheduleType: mandate.sddScheduleType,
        scheduleTotalCollectionCount: mandate.sddScheduleCount,
      });
      const trackingProperties = getTrackingProperties(false);
      segment.track('public-page_sdd-payment_loaded', trackingProperties);

      // Initialize form values
      setValue('clientAddress', mandate.clientAddress);
      setValue('clientZipCode', mandate.clientZipCode);
      setValue('clientCity', mandate.clientCity);
      setValue('clientCountryCode', mandate.clientCountryCode);
      setValue('clientIban', mandate.clientIban);
      setValue('clientProvinceCode', mandate.clientProvinceCode);

      // Initialize readonly fields
      setReadonlyClientAddress(mandate.clientAddress || undefined);
      setReadonlyClientZipCode(mandate.clientZipCode || undefined);
      setReadonlyClientCity(mandate.clientCity || undefined);
      setReadonlyClientCountryCode(mandate.clientCountryCode || undefined);
    }
  }, [mandate, localeManager, segment, setValue]);

  const isRequiredField = (
    fieldName: keyof SignMandateFormData,
    currentFormValues: SignMandateFormData
  ): boolean => {
    const alwaysRequiredFields: (keyof SignMandateFormData)[] = [
      'clientAddress',
      'clientZipCode',
      'clientCity',
      'clientCountryCode',
      'clientIban',
    ];

    if (alwaysRequiredFields.includes(fieldName)) {
      return true;
    }

    // Province is required only for Italy
    if (fieldName === 'clientProvinceCode') {
      return currentFormValues.clientCountryCode === 'IT';
    }

    return false;
  };

  const onUpdate = (formField: string, value: string) => {
    // from "form field" to corresponding "model field"
    const fieldMap: Record<string, keyof SignMandateFormData> = {
      address: 'clientAddress',
      zipcode: 'clientZipCode',
      city: 'clientCity',
      country: 'clientCountryCode',
      iban: 'clientIban',
      province: 'clientProvinceCode',
    };

    const formFieldName = fieldMap[formField];

    // Validate form field
    if (formFieldName && mandate) {
      setValue(formFieldName, value);

      if (fieldErrors[formFieldName] && value) {
        clearFieldError(formFieldName);
      }

      if (!value && isRequiredField(formFieldName, formValues)) {
        setFieldError(formFieldName, ['required']);
      }

      if (formField === 'country' && value === 'IT') {
        const currentData = { ...formValues, clientCountryCode: 'IT' };
        addClientSideErrors(currentData);
      }
    }
  };

  const onSubmit = (data: SignMandateFormData) => {
    if (!mandate) return;

    clearFieldErrors();
    setTermsAndConditionsError(undefined);

    const hasTermsAndConditionsError = !data.termsAndConditions;
    if (hasTermsAndConditionsError) {
      setTermsAndConditionsError('required');
    }

    const hasClientSideErrors = addClientSideErrors(data);
    if (hasClientSideErrors || hasTermsAndConditionsError) {
      return;
    }

    signMandate({
      mandateId: mandate.mandateId,
      clientIban: data.clientIban,
      clientAddress: data.clientAddress,
      clientCity: data.clientCity,
      clientZipCode: data.clientZipCode,
      clientProvinceCode: data.clientProvinceCode,
      clientCountryCode: data.clientCountryCode,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- service not typed
      deviceId: deviceManager.id,
    });
  };

  const handleTermsAndConditionsChange = () => {
    const newValue = !formValues.termsAndConditions;
    setValue('termsAndConditions', newValue);

    if (newValue) {
      setTermsAndConditionsError(undefined);
    } else {
      setTermsAndConditionsError('required');
    }
  };

  if (isLoading) {
    return <Loading spinnerSize="large" />;
  }

  if ((isError && errorType) || !mandate) {
    return (
      <div className={styles.fullScreenPage}>
        <Error
          errorType={errorType || 'generic_error'}
          onRetry={errorType === 'generic_error' ? () => refetch() : undefined}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageContainer}>
        <form className="mb-32" onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.formFieldsContainer}>
            <div className="mb-32">
              <Header
                amount={mandate.sddAmount}
                dueDate={mandate.sddCollectionDate}
                isOverdue={mandate.sddIsOverdue}
                isRecurring={mandate.isSddRecurring}
                organizationLogoUrl={mandate.organizationLogoUrl}
                organizationName={mandate.organizationName}
                recurringFrequency={mandate.sddScheduleType}
                recurringInterval={mandate.sddCustomScheduleInterval}
                recurringUnit={mandate.sddCustomScheduleUnit}
                reference={mandate.sddReference}
              />
            </div>
            <Details
              address={formValues.clientAddress}
              addressErrors={fieldErrors.clientAddress?.map((code: string) => ({ message: code }))}
              city={formValues.clientCity}
              cityErrors={fieldErrors.clientCity?.map((code: string) => ({ message: code }))}
              country={formValues.clientCountryCode}
              countryErrors={fieldErrors.clientCountryCode?.map((code: string) => ({
                message: code,
              }))}
              email={mandate.clientEmail}
              iban={formValues.clientIban}
              ibanErrors={fieldErrors.clientIban?.map((code: string) => ({ message: code }))}
              name={mandate.clientName}
              onUpdate={onUpdate}
              province={formValues.clientProvinceCode}
              provinceErrors={fieldErrors.clientProvinceCode?.map((code: string) => ({
                message: code,
              }))}
              readonlyAddress={readonlyClientAddress}
              readonlyCity={readonlyClientCity}
              readonlyCountry={readonlyClientCountryCode}
              readonlyZipcode={readonlyClientZipCode}
              zipcode={formValues.clientZipCode}
              zipcodeErrors={fieldErrors.clientZipCode?.map((code: string) => ({ message: code }))}
            />
          </div>

          <div className={styles.mobileContainer}>
            <ToggleSection
              address={mandate.organizationAddress}
              creditorIdentifier={mandate.organizationCreditorIdentifier}
              creditorName={mandate.organizationName}
              reference={mandate.sddReference}
              signatureDate={signatureDate}
              uniqueMandateReference={mandate.mandateReferenceCode}
            />
            <TermsAndConditionsCheckbox
              creditorName={mandate.organizationName}
              errorMessage={formatMessage({
                id: 'sdd-collections.payment-link-page.mandate.required-disclaimer',
              })}
              isInvalid={Boolean(termsAndConditionsError)}
              isSelected={formValues.termsAndConditions}
              onChange={handleTermsAndConditionsChange}
            />
            <div>
              <button
                className={cx('btn', 'btn--primary', styles.submitButton)}
                data-test-submit
                disabled={isSubmitting || isSigningMandate}
                type="submit"
              >
                <FormattedMessage id="sdd-collections.payment-link-page.authorize-cta" />
                {isSubmitting || isSigningMandate ? (
                  <Loading className="ml-8" spinnerSize="small" />
                ) : null}
              </button>
            </div>
          </div>
        </form>
        <Footer />
      </div>
    </div>
  );
}
